import {takeLatest, call, put, all} from "redux-saga/effects";
import {SagaIterator} from "redux-saga";
import {PayloadAction} from "@reduxjs/toolkit";
import api from "../../api/axiosConfig";
import {AxiosResponse} from "axios";
import {
  checkAuthSession,
  login as loginApi,
  logout as logoutApi,
  logoutLocalOnly,
  type AuthSessionCheckResult,
  type UserProfileDto,
} from "../../api/userApi";
import {disableWebPush, disableWebPushLocalOnly} from "../../components/live/webPushClient";

import {
  loginRequest,
  loginSuccess,
  authFailure,
  signupRequest,
  signupSuccess,
  signupFailure,
  setUserInfo,
  userInfoRequest,
  findEmailRequest,
  findEmailSuccess,
  findEmailFailure,
  requestPasswordResetRequest,
  requestPasswordResetSuccess,
  requestPasswordResetFailure,
  requestSmsCodeRequest,
  requestSmsCodeSuccess,
  requestSmsCodeFailure,
  verifySmsCodeRequest,
  verifySmsCodeSuccess,
  verifySmsCodeFailure,
  SignupPayload,
  FindEmailPayload,
  RequestResetPayload,
  RequestSmsCodePayload,
  VerifySmsCodePayload,
  logoutRequest,
  logoutSuccess,
  sessionExpiredLogout,
  restoreSessionRequest,
  guestSessionInitialized,
  authCheckFailure,
} from "../slice/authSlice";

type LoginApiResponse = {
  id: number;
  email: string;
  token?: string;
  accessToken?: string;
  expiresAt?: number;
  expiresIn?: number;
};

function resolveExpiresAt(responseData: LoginApiResponse): number {
  const now = Date.now();

  if (responseData?.expiresAt && Number.isFinite(Number(responseData.expiresAt))) {
    return Number(responseData.expiresAt);
  }

  if (responseData?.expiresIn && Number.isFinite(Number(responseData.expiresIn))) {
    return now + Number(responseData.expiresIn) * 1000;
  }

  return now + 30 * 60 * 1000;
}

function getLoginErrorMessage(e: any): string {
  const status = e?.response?.status;

  if (status === 401) return "비밀번호가 올바르지 않거나 세션이 만료되었습니다.";
  if (status === 404) return "존재하지 않는 계정입니다.";
  if (status === 400) return "이메일과 비밀번호를 입력해주세요.";
  return "로그인에 실패했습니다.";
}

function getUserFetchErrorMessage(e: any): string {
  const status = e?.response?.status;
  if (status === 401 || status === 419) return "세션이 만료되었습니다. 다시 로그인해주세요.";
  if (status === 403) return "인증 권한이 없습니다.";
  return "로그인 정보를 가져오지 못했습니다.";
}

function extractServerMessage(e: any): string {
  const data = e?.response?.data;

  if (typeof data === "string") {
    return data.trim();
  }

  if (data && typeof data === "object") {
    const candidates = [
      data.message,
      data.error_description,
      data.detail,
      data.error,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }
  }

  return "";
}

function toUserInfoPayload(user: UserProfileDto) {
  return {
    id: user.id,
    email: user.email,
    phone: user.phone ?? null,
    marketingEmailAgree: user.marketingEmailAgree ?? false,
    marketingSmsAgree: user.marketingSmsAgree ?? false,
    role: user.role ?? null,
    googleConnected: user.googleConnected ?? false,
    kakaoConnected: user.kakaoConnected ?? false,
    naverConnected: user.naverConnected ?? false,
    isActive: user.isActive ?? true,
  };
}

function* handleLogin(action: PayloadAction<{ email: string; password: string }>): SagaIterator {
  try {
    const data: LoginApiResponse = yield call(loginApi, action.payload);

    const token = data.token ?? data.accessToken;
    const expiresAt = resolveExpiresAt(data);

    yield put(
        loginSuccess({
          id: data.id,
          email: data.email,
          token,
          expiresAt,
        })
    );
  } catch (e: any) {
    yield put(authFailure(getLoginErrorMessage(e)));
  }
}

function* handleSignup(action: PayloadAction<SignupPayload>): SagaIterator {
  try {
    const p = action.payload;

    if (
        !p.agreeTerms ||
        !p.agreePrivacy ||
        !p.agreeDisclaimer ||
        !p.ageOver14Agree
    ) {
      yield put(
          signupFailure(
              "필수 항목(약관/개인정보/서비스 고지/만 14세 이상)에 동의해야 합니다."
          )
      );
      return;
    }

    if (
        !p.consentVersions ||
        !p.consentVersions.terms?.trim() ||
        !p.consentVersions.privacy?.trim() ||
        !p.consentVersions.disclaimer?.trim() ||
        !p.consentVersions.ageOver14?.trim() ||
        !p.consentVersions.marketing?.trim()
    ) {
      yield put(signupFailure("동의 버전 정보가 누락되었습니다."));
      return;
    }

    if (!p.turnstileToken?.trim()) {
      yield put(signupFailure("봇 방지 검증이 누락되었습니다."));
      return;
    }

    const signupData = {
      email: p.email,
      password: p.password,
      ...(p.phone ? {phone: p.phone} : {}),

      agreeTerms: p.agreeTerms,
      agreePrivacy: p.agreePrivacy,
      agreeDisclaimer: p.agreeDisclaimer,
      ageOver14Agree: p.ageOver14Agree,

      marketingEmailAgree: p.marketingEmailAgree,
      marketingSmsAgree: p.marketingSmsAgree,

      consentVersions: {
        terms: p.consentVersions.terms,
        privacy: p.consentVersions.privacy,
        disclaimer: p.consentVersions.disclaimer,
        ageOver14: p.consentVersions.ageOver14,
        marketing: p.consentVersions.marketing,
      },

      turnstileToken: p.turnstileToken,
    };

    const response: AxiosResponse<string> = yield call(api.post, "/user/signup", signupData);
    alert(
        typeof response.data === "string" && response.data.trim()
            ? response.data
            : "회원가입이 완료되었습니다. 이메일 인증 링크를 확인해 주세요."
    );
    yield put(signupSuccess());
  } catch (e: any) {
    const status = e?.response?.status;
    const serverMessage = extractServerMessage(e);

    let message = serverMessage || "회원가입에 실패했습니다.";

    if (status === 409) {
      if (
          serverMessage.includes("최신") ||
          serverMessage.includes("약관") ||
          serverMessage.includes("정책") ||
          serverMessage.includes("동의")
      ) {
        message = serverMessage || "최신 약관/정책 버전에 대한 동의가 필요합니다.";
      } else {
        message = serverMessage || "이미 가입된 이메일입니다.";
      }
    } else if (status === 400) {
      message = serverMessage || "입력 형식이 올바르지 않습니다.";
    } else if (status === 401) {
      message = serverMessage || "회원가입 요청이 인증 단계에서 차단되었습니다.";
    } else if (status === 429) {
      message = "요청이 너무 많습니다. 잠시 후 다시 시도해주세요.";
    } else if (status === 500) {
      message = serverMessage || "서버 설정 오류로 회원가입에 실패했습니다.";
    } else if (status === 502) {
      message = serverMessage || "봇 방지 검증 서버와 통신하지 못했습니다. 잠시 후 다시 시도해주세요.";
    }

    yield put(signupFailure(message));
  }
}

function* handleFetchUser(): SagaIterator {
  try {
    const response: AxiosResponse<UserProfileDto> = yield call(api.get, "/user/me");
    yield put(setUserInfo(toUserInfoPayload(response.data)));
  } catch (e: any) {
    const status = e?.response?.status;

    if (status === 401 || status === 419) {
      try {
        yield call(disableWebPushLocalOnly);
      } catch {
        // Push cleanup must not block auth-state cleanup.
      }
      yield call(logoutLocalOnly);
      yield put(sessionExpiredLogout("로그인 세션이 만료되었습니다. 다시 로그인하면 현재 보던 화면부터 이어집니다."));
      return;
    }

    // Network/5xx is not proof that the refresh/session is invalid. Keep the UI
    // fail-closed instead of converting cached local state into authentication.
    yield put(authCheckFailure(getUserFetchErrorMessage(e)));
  }
}

function* handleFindEmail(action: PayloadAction<FindEmailPayload>): SagaIterator {
  try {
    const phone = action.payload.phone?.trim();

    if (!phone) {
      yield put(findEmailFailure("휴대폰 번호를 입력해주세요."));
      return;
    }

    const response: AxiosResponse = yield call(api.post, "/user/find-email", {phone});

    const maskedEmail =
        response.data?.maskedEmail ??
        response.data?.emailMasked ??
        response.data?.email ??
        null;

    const success = response.data?.success ?? true;
    const message =
        response.data?.message ??
        "입력하신 정보와 일치하는 계정이 있는 경우 안내가 제공됩니다.";

    if (!success) {
      yield put(findEmailFailure(message));
      return;
    }

    yield put(
        findEmailSuccess({
          maskedEmail,
          message,
        })
    );
  } catch (e: any) {
    const status = e?.response?.status;
    let message = "아이디 찾기에 실패했습니다.";

    if (status === 400) message = "휴대폰 번호 형식이 올바르지 않습니다.";

    yield put(findEmailFailure(message));
  }
}

function* handleRequestPasswordReset(action: PayloadAction<RequestResetPayload>): SagaIterator {
  try {
    const email = action.payload.email?.trim();

    if (!email) {
      yield put(requestPasswordResetFailure("이메일을 입력해주세요."));
      return;
    }

    yield call(api.post, "/user/request-password-reset", {email});
    yield put(requestPasswordResetSuccess());
  } catch (e: any) {
    const status = e?.response?.status;
    let message = "재설정 링크 발송에 실패했습니다.";

    if (status === 404) message = "존재하지 않는 계정입니다.";
    else if (status === 400) message = "이메일 형식이 올바르지 않습니다.";

    yield put(requestPasswordResetFailure(message));
  }
}

function* handleRequestSmsCode(action: PayloadAction<RequestSmsCodePayload>): SagaIterator {
  try {
    const phone = action.payload.phone?.trim();
    const purpose = action.payload.purpose;

    if (!phone) {
      yield put(requestSmsCodeFailure("휴대폰 번호를 입력해주세요."));
      return;
    }

    yield call(api.post, "/user/sms/request", {phone, purpose});
    yield put(requestSmsCodeSuccess());
  } catch (e: any) {
    const status = e?.response?.status;
    let message = "인증번호 발송에 실패했습니다.";

    if (status === 429) message = "요청이 너무 많습니다. 잠시 후 다시 시도해주세요.";
    else if (status === 400) message = "휴대폰 번호 형식이 올바르지 않습니다.";

    yield put(requestSmsCodeFailure(message));
  }
}

function* handleVerifySmsCode(action: PayloadAction<VerifySmsCodePayload>): SagaIterator {
  try {
    const phone = action.payload.phone?.trim();
    const code = action.payload.code?.trim();
    const purpose = action.payload.purpose;

    if (!phone) {
      yield put(verifySmsCodeFailure("휴대폰 번호가 비어 있습니다."));
      return;
    }

    if (!code) {
      yield put(verifySmsCodeFailure("인증번호를 입력해주세요."));
      return;
    }

    yield call(api.post, "/user/sms/verify", {phone, code, purpose});
    yield put(verifySmsCodeSuccess());
  } catch (e: any) {
    const status = e?.response?.status;
    let message = "인증번호 확인에 실패했습니다.";

    if (status === 401 || status === 403) message = "인증번호가 올바르지 않거나 만료되었습니다.";
    else if (status === 400) message = "인증번호 형식이 올바르지 않습니다.";

    yield put(verifySmsCodeFailure(message));
  }
}

function broadcastLogoutToOtherTabs(): void {
  try {
    localStorage.setItem(
        "policy-intel:auth-event",
        JSON.stringify({type: "logout", at: Date.now()})
    );
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }
}

function* handleLogout(): SagaIterator {
  try {
    // Must happen while the authenticated access token still exists.
    // disableWebPush() always attempts the local browser unsubscribe even if
    // the server-side endpoint disable request fails.
    try {
      yield call(disableWebPush);
    } catch {
      // Logout itself must still proceed. Expired endpoints are also disabled
      // server-side on a later 404/410 push response.
    }

    yield call(logoutApi);
  } catch {
    yield call(logoutLocalOnly);
  } finally {
    yield put(logoutSuccess());

    // Broadcast only after push cleanup + auth cleanup. This avoids another tab
    // clearing shared auth storage before /web-push/unsubscribe is attempted.
    yield call(broadcastLogoutToOtherTabs);
  }
}

function* handleRestoreSession(): SagaIterator {
  try {
    const result: AuthSessionCheckResult = yield call(checkAuthSession);

    if (result.state === "AUTHENTICATED" && result.user) {
      yield put(setUserInfo(toUserInfoPayload(result.user)));
      return;
    }

    if (result.state === "SESSION_EXPIRED") {
      try {
        yield call(disableWebPushLocalOnly);
      } catch {
        // Best-effort local cleanup only.
      }
      yield call(logoutLocalOnly);
      yield put(
          sessionExpiredLogout(
              "로그인 세션이 만료되었습니다. 다시 로그인하면 현재 보던 화면부터 이어집니다."
          )
      );
      return;
    }

    // No local identity existed and /api/user/me confirmed no authenticated user.
    yield put(guestSessionInitialized());
  } catch (e: any) {
    yield put(authCheckFailure(getUserFetchErrorMessage(e)));
  }
}

export default function* authSaga(): SagaIterator {
  yield all([
    takeLatest(loginRequest.type, handleLogin),
    takeLatest(signupRequest.type, handleSignup),
    takeLatest(userInfoRequest.type, handleFetchUser),

    takeLatest(findEmailRequest.type, handleFindEmail),
    takeLatest(requestPasswordResetRequest.type, handleRequestPasswordReset),

    takeLatest(requestSmsCodeRequest.type, handleRequestSmsCode),
    takeLatest(verifySmsCodeRequest.type, handleVerifySmsCode),

    takeLatest(logoutRequest.type, handleLogout),
    takeLatest(restoreSessionRequest.type, handleRestoreSession),
  ]);
}

